// @TODO - This file to be removed when `tmp-use-slsx-session-header` is retired
import React, { FunctionComponent, useEffect, useContext } from "react";
import { RouteComponentProps, withRouter } from "react-router";
import URI from "urijs";
import { useFlags } from "launchdarkly-react-client-sdk";
import { ActionType, MctToastType, UserLanguage } from "../../@types";
import {
  fetchCurrentPlan,
  logError,
  useTranslate,
  parseSearchParams,
  parseLangFromRelayParam,
} from "../../helpers";
import {
  loginBene,
  getBeneDrugs,
  getBenePharmacies,
  getLoggedInBeneInfo,
} from "../../api";
import { AppContext } from "../../app/store";
import routes from "../../app/routes";
import { AppPage, LoadingMask } from "../../components";
import { fetchPlanIdsForNextYear } from "../../helpers/planHelpers";
import { getToastRoute } from "../../helpers";
import {
  hasLocalSession,
  parseDegradedIntegrations,
} from "../../helpers/loginHelpers";
import {
  getPharmacyTypeFromSavedPharmacies,
  updateBeneInfo,
} from "../../helpers/beneficiaryInfoHelpers";
import { getYearPartInfo } from "../../helpers/yearPartHelpers";
import { ApiError } from "../../helpers/errors";

const LoginCallbackHandler: FunctionComponent<RouteComponentProps> = props => {
  const t = useTranslate();
  const flags = useFlags();
  const {
    isOutsideOpenEnrollment,
    isOpenEnrollmentNextYearOnly,
  } = getYearPartInfo(flags);

  // The SLS code, which is transmited to us via query param (aka the 'search' portion of the location object)
  const { search } = props.location;

  const { req_token, relay } = parseSearchParams(URI.parseQuery(search));
  const code = req_token;
  const { state, dispatch } = useContext(AppContext);

  const sendToFromIfExists = () => {
    if (props.history.location.state) {
      const { from } = props.history.location.state as { from?: string };
      from && props.history.replace(from);
    }
  };

  const getBeneficiary = async (): Promise<void> => {
    let userLang = UserLanguage.ENGLISH;
    const needsUserRefresh = hasLocalSession() && !state.beneficiary;

    if (relay) {
      const relayLang = parseLangFromRelayParam(relay);
      userLang = relayLang || userLang;
    }

    if (code || needsUserRefresh) {
      // Clear out any previous bene info, if not already cleared via sessionStorage
      code && dispatch({ type: ActionType.RESET_STATE });

      try {
        const { beneficiary: beneInfo, headers } = code
          ? await loginBene(code)
          : await getLoggedInBeneInfo();

        const degradedIntegrations = parseDegradedIntegrations(headers);

        if (degradedIntegrations.length) {
          dispatch({
            type: ActionType.UPDATE_DEGRADED_INTEGRATIONS,
            payload: degradedIntegrations,
          });
        }

        updateBeneInfo({ beneInfo, dispatch });

        try {
          const drugs = await getBeneDrugs();
          dispatch({ type: ActionType.SET_PRESCRIPTIONS, payload: drugs });
        } catch (e) {
          logError(
            "Failed to get beneficiary drugs (LoginCallbackHandler)",
            e as ApiError
          );
        }

        try {
          const { pharmacies, mail_order } = await getBenePharmacies();
          dispatch({ type: ActionType.SET_PHARMACIES, payload: pharmacies });

          dispatch({
            type: ActionType.UPDATE_PHARMACY_TYPE,
            payload: getPharmacyTypeFromSavedPharmacies(pharmacies, mail_order),
          });
        } catch (e) {
          logError(
            "Failed to get beneficiary pharmacies (LoginCallbackHandler)",
            e as ApiError
          );
        }

        if (beneInfo?.current_coverage.length) {
          let nextYearPlanIds;
          if (!isOutsideOpenEnrollment || isOpenEnrollmentNextYearOnly) {
            nextYearPlanIds = await fetchPlanIdsForNextYear(
              beneInfo.current_coverage[0],
              "LoginCallbackHandler"
            );
          }
          await fetchCurrentPlan(
            beneInfo,
            state.pharmacies,
            beneInfo.lis_level,
            dispatch,
            nextYearPlanIds,
            beneInfo.future_lis_level
          );
        }

        if (needsUserRefresh) {
          sendToFromIfExists();
        } else {
          props.history.replace(
            `${routes.summary.landingPage}?lang=${userLang}`
          );
        }
      } catch (e) {
        logError(
          "Failed to retrieve beneficiary (LoginCallbackHandler)",
          e as ApiError
        );

        if (needsUserRefresh) {
          sendToFromIfExists();
        } else {
          props.history.replace(
            getToastRoute(
              `${routes.questionRouting}?lang=${userLang}`,
              MctToastType.BENE_FAILED_LOGIN
            )
          );
        }
      }
    } else if (state.beneficiary) {
      props.history.replace(`${routes.summary.landingPage}?lang=${userLang}`);
    } else {
      props.history.replace(
        getToastRoute(
          `${routes.questionRouting}?lang=${userLang}`,
          MctToastType.BENE_FAILED_LOGIN
        )
      );
    }
  };

  useEffect(() => {
    getBeneficiary();
  }, [flags]);

  return (
    <AppPage>
      <LoadingMask
        loading={true}
        message={t("loading.retrieving_your_information")}
      />
    </AppPage>
  );
};

export default withRouter(LoginCallbackHandler);
